/**
 * Capacite de bus **`klavox.credentials`** v1 — le RECEPTEUR de bout en bout.
 *
 * Contrat : `bus/klavox-credentials.v1.md`. `credentials-transfer.ts` est la
 * porte (il lit l'enveloppe comme une donnee non fiable) ; ce module est ce qui
 * se passe derriere elle :
 *
 *   offre      -> coffre ferme ? `coffre-non-connecte` ; sinon fenetre de
 *                 consentement ; non -> `refuse` (et le pair n'est plus
 *                 sollicite de la session) ; oui -> paire X25519 EPHEMERE,
 *                 cle privee non extractible, en memoire, 5 min, `pret` ;
 *   livraison  -> la cle privee est RETIREE avant toute tentative
 *                 d'ouverture (une charge ne s'ouvre qu'une fois, qu'elle
 *                 reussisse ou non), ouverture HPKE (RFC 9180, mode base,
 *                 `info` = nom du contrat, `aad` = transferId), lecture
 *                 `parseCxfHeader`, versement par l'import existant (qui
 *                 rechiffre sous la vaultKey), puis `result` avec les verdicts
 *                 `credentials.audit` des SEULES origines du transfert.
 *
 * HPKE n'est PAS compose ici : `@hpke/core` (implementation de reference
 * RFC 9180, primitives WebCrypto natives, une seule dependance `@hpke/common`)
 * fait tout le KEM/KDF/AEAD. Une seule suite, fixee par le contrat : pas de
 * negociation, donc pas de retrogradation.
 *
 * Rien de la charge ne sort d'ici : ni journal, ni message d'erreur, ni
 * stockage. Le `result` est relu tel qu'il partira et refuse s'il cite un
 * secret transfere (`assertNoTransferredSecret`).
 */
import { Aes256Gcm, CipherSuite, DhkemX25519HkdfSha256, HkdfSha256 } from '@hpke/core';
import type { ImportedEntry } from './csv-import.js';
import type { CredentialsAuditVerdict } from './credentials-audit.js';
import { parseCxfHeader } from './cxf.js';
import {
  CREDENTIALS_TRANSFER_CAPABILITY,
  CREDENTIALS_TRANSFER_SUITE,
  CREDENTIALS_TRANSFER_VERSION,
  parseCredentialsTransfer,
  type CredentialsTransferDelivery,
  type CredentialsTransferOffer,
} from './credentials-transfer.js';
import { extractDomain } from '../ui/render-utils.js';

/** Duree de vie d'une cle ephemere apres `pret` (contrat § 2.2). */
export const CREDENTIALS_TRANSFER_TTL_MS = 5 * 60 * 1000;

/** `info` HPKE (contrat § 1) : le nom du contrat, qui lie la cle derivee a CE protocole. */
export const CREDENTIALS_TRANSFER_INFO = 'klavox-credentials.v1';
const INFO = new TextEncoder().encode(CREDENTIALS_TRANSFER_INFO);

type Entete = { readonly capability: typeof CREDENTIALS_TRANSFER_CAPABILITY; readonly version: typeof CREDENTIALS_TRANSFER_VERSION };

export type CredentialsTransferStatus = Entete & { readonly op: 'status' } & (
    | { readonly etat: 'coffre-non-connecte' | 'refuse' }
    | {
        readonly etat: 'pret';
        readonly transferId: string;
        readonly suite: typeof CREDENTIALS_TRANSFER_SUITE;
        readonly publicKey: string;
        readonly expiresAt: number;
      }
    | { readonly etat: 'expire' | 'echec'; readonly transferId: string }
  );

export type CredentialsTransferResult = Entete & {
  readonly op: 'result';
  readonly transferId: string;
  readonly etat: 'importe';
  readonly importes: number;
  readonly ignores: number;
  readonly verdicts: readonly CredentialsAuditVerdict[];
};

export type CredentialsTransferReply = CredentialsTransferStatus | CredentialsTransferResult;

/** Ce que le coffre (l'UI) prete au recepteur — jamais persiste ici. */
export interface CredentialsTransferHost {
  /** Coffre deverrouille sur cet appareil. */
  deverrouille(): boolean;
  /** Fenetre de consentement : `true` seulement sur le geste explicite de l'utilisateur. */
  consentir(offre: CredentialsTransferOffer): Promise<boolean>;
  /** Chemin d'import existant (rechiffre sous la vaultKey) ; rend le nombre d'entrees enregistrees. */
  importer(entrees: readonly ImportedEntry[]): Promise<number>;
  /** Verdicts `credentials.audit` pour ces origines, calcules APRES le versement. */
  verdicts(origines: readonly string[]): readonly CredentialsAuditVerdict[];
  /** Horloge (ms) ; `Date.now` par defaut. */
  now?(): number;
}

export class CredentialsTransferLeakError extends Error {
  constructor() {
    super('klavox.credentials : un secret transfere allait partir dans le resultat');
    this.name = 'CredentialsTransferLeakError';
  }
}

interface Attente {
  readonly cles: CryptoKeyPair;
  readonly expiresAt: number;
  readonly minuterie: ReturnType<typeof setTimeout>;
}

const suite = new CipherSuite({ kem: new DhkemX25519HkdfSha256(), kdf: new HkdfSha256(), aead: new Aes256Gcm() });

function b64u(octets: Uint8Array): string {
  let binaire = '';
  for (const octet of octets) binaire += String.fromCharCode(octet);
  return btoa(binaire).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function entete(): Entete {
  return { capability: CREDENTIALS_TRANSFER_CAPABILITY, version: CREDENTIALS_TRANSFER_VERSION };
}

/** Nombre d'elements annonces par la charge : ce qui n'a pas ete enregistre est `ignores`. */
function elementsDeLaCharge(charge: unknown): number {
  if (typeof charge !== 'object' || charge === null) return 0;
  const comptes = (charge as { accounts?: unknown }).accounts;
  if (!Array.isArray(comptes)) return 0;
  return comptes.reduce<number>((total, compte) => {
    const items = typeof compte === 'object' && compte !== null ? (compte as { items?: unknown }).items : undefined;
    return total + (Array.isArray(items) ? items.length : 0);
  }, 0);
}

/** Origines du transfert : le pair les connaissait deja, rien d'autre du coffre ne sort. */
export function originesDuTransfert(entrees: readonly ImportedEntry[]): string[] {
  const origines = new Set<string>();
  for (const entree of entrees) {
    for (const uri of entree.uris) {
      const hote = extractDomain(uri).toLowerCase();
      if (hote !== '') origines.add(hote);
    }
  }
  return [...origines].sort();
}

/**
 * GARDE-FOU DUR : le `result` est relu tel qu'il partira sur le canal, et
 * refuse s'il cite une valeur secrete d'une entree transferee. Une valeur
 * egale a une origine du transfert est exemptee (c'est la seule donnee que
 * le contrat publie) — l'exemption se calcule sur les ENTREES, jamais sur la
 * reponse, sinon une reponse fautive s'exempterait elle-meme.
 */
export function assertNoTransferredSecret(reponse: CredentialsTransferResult, entrees: readonly ImportedEntry[]): void {
  const serialise = JSON.stringify(reponse);
  const origines = new Set(originesDuTransfert(entrees));
  for (const entree of entrees) {
    const valeurs = [
      entree.name,
      entree.username,
      entree.password,
      entree.notes,
      entree.totpSecret,
      ...(entree.customFields ?? []).flatMap((champ) => [champ.name, champ.value]),
      ...(entree.passkeys ?? []).map((passkey) => passkey.privateKey),
    ];
    for (const valeur of valeurs) {
      if (typeof valeur !== 'string' || valeur.length < 4 || origines.has(valeur.toLowerCase())) continue;
      if (serialise.includes(valeur)) throw new CredentialsTransferLeakError();
    }
  }
}

export class CredentialsTransferInbox {
  private readonly attentes = new Map<string, Attente>();
  /** Sources refusees dans la session : le pair ne redemande pas (contrat § 2.2). */
  private readonly refusees = new Set<string>();
  private consentementOuvert = false;

  constructor(private readonly hote: CredentialsTransferHost) {}

  private now(): number {
    return this.hote.now?.() ?? Date.now();
  }

  /**
   * Point d'entree UNIQUE du canal. Rend la reponse a poster, ou `null` pour
   * tout ce qui n'appelle pas de reponse : message d'une autre capacite, nos
   * propres `status`/`result` relus sur le canal, enveloppe refusee par la
   * porte (un refus ne cite jamais la charge, et ne repond pas davantage).
   */
  async receive(brut: unknown): Promise<CredentialsTransferReply | null> {
    const lu = parseCredentialsTransfer(brut);
    if (!lu.ok) return null;
    return lu.message.op === 'offer' ? this.offre(lu.message) : this.livraison(lu.message);
  }

  /** Verrouillage du coffre : toute cle ephemere est oubliee, les livraisons en vol expirent. */
  forget(): void {
    for (const attente of this.attentes.values()) clearTimeout(attente.minuterie);
    this.attentes.clear();
  }

  private async offre(offre: CredentialsTransferOffer): Promise<CredentialsTransferStatus | null> {
    if (!this.hote.deverrouille()) return { ...entete(), op: 'status', etat: 'coffre-non-connecte' };
    if (this.refusees.has(offre.source.id)) return { ...entete(), op: 'status', etat: 'refuse' };
    // Une fenetre a la fois : une seconde offre pendant la premiere n'en ouvre pas une autre.
    if (this.consentementOuvert) return null;
    this.consentementOuvert = true;
    let accepte: boolean;
    try {
      accepte = await this.hote.consentir(offre);
    } finally {
      this.consentementOuvert = false;
    }
    if (!accepte) {
      this.refusees.add(offre.source.id);
      return { ...entete(), op: 'status', etat: 'refuse' };
    }
    // Le coffre a pu etre verrouille pendant que la fenetre etait ouverte.
    if (!this.hote.deverrouille()) return { ...entete(), op: 'status', etat: 'coffre-non-connecte' };

    // Cle privee NON extractible : elle ne peut que deriver, jamais etre relue.
    const cles = (await crypto.subtle.generateKey({ name: 'X25519' }, false, ['deriveBits'])) as CryptoKeyPair;
    const publicKey = new Uint8Array(await crypto.subtle.exportKey('raw', cles.publicKey));
    const transferId = b64u(crypto.getRandomValues(new Uint8Array(16)));
    const expiresAt = this.now() + CREDENTIALS_TRANSFER_TTL_MS;
    const minuterie = setTimeout(() => this.attentes.delete(transferId), CREDENTIALS_TRANSFER_TTL_MS);
    this.attentes.set(transferId, { cles, expiresAt, minuterie });
    return {
      ...entete(),
      op: 'status',
      etat: 'pret',
      transferId,
      suite: CREDENTIALS_TRANSFER_SUITE,
      publicKey: b64u(publicKey),
      expiresAt,
    };
  }

  private async livraison(livraison: CredentialsTransferDelivery): Promise<CredentialsTransferReply> {
    const { transferId } = livraison;
    const attente = this.attentes.get(transferId);
    // Usage unique : la cle est retiree AVANT toute tentative, reussie ou non.
    this.attentes.delete(transferId);
    if (attente !== undefined) clearTimeout(attente.minuterie);
    if (attente === undefined || attente.expiresAt <= this.now()) return { ...entete(), op: 'status', etat: 'expire', transferId };

    let entrees: ImportedEntry[];
    let annonces: number;
    try {
      const clair = await suite.open(
        { recipientKey: attente.cles, enc: livraison.enc, info: INFO },
        livraison.ciphertext,
        new TextEncoder().encode(transferId),
      );
      const charge: unknown = JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(clair));
      new Uint8Array(clair).fill(0);
      entrees = parseCxfHeader(charge);
      annonces = Math.max(elementsDeLaCharge(charge), entrees.length);
    } catch {
      // Le message d'erreur pourrait citer la charge : on ne garde que l'etat.
      return { ...entete(), op: 'status', etat: 'echec', transferId };
    }
    if (!this.hote.deverrouille()) return { ...entete(), op: 'status', etat: 'echec', transferId };

    let importes: number;
    try {
      importes = await this.hote.importer(entrees);
    } catch {
      // Rien n'est annonce comme importe : le pair garde sa copie, rien n'est perdu.
      return { ...entete(), op: 'status', etat: 'echec', transferId };
    }
    const origines = new Set(originesDuTransfert(entrees));
    const resultat: CredentialsTransferResult = {
      ...entete(),
      op: 'result',
      transferId,
      etat: 'importe',
      importes,
      ignores: Math.max(0, annonces - importes),
      verdicts: this.hote.verdicts([...origines]).filter((verdict) => origines.has(verdict.origin)),
    };
    assertNoTransferredSecret(resultat, entrees);
    return resultat;
  }
}
